import { PureComponent, ComponentType } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';

import TextField from '../components/TextField/TextField';
import ExpressionSelector from './ExpressionSelector/ExpressionSelector';
import ListExpression from './ListExpression';
import FnExpression from './FnExpression';
import Select from '../components/Select';
import WithFormulaData from '../decorators/WithFormulaData';
import { getRandomName } from '../utils/utils';
import type { ExpressionComponent } from '../utils/utils';
import type { Dispatch } from 'redux';
import type { FormulaNode, UiFormulaNode } from '../typeDefs';
import type { RootState } from '../redux/store';

const makeGetStub = ($op: string) => (arg: unknown[] = []): FormulaNode => {
    const [$value, ...$args] = arg;
    return { $op, $value, $args };
};

interface CheckerMessageProps {
    data: UiFormulaNode;
    placeholder?: string;
    path?: string;
    dispatch?: Dispatch;
    style?: React.CSSProperties;
    [key: string]: unknown;
}

export class CheckerMessage extends PureComponent<CheckerMessageProps> {
    static defaultProps = {
        placeholder: 'text:',
    };

    static getStub = (arg: unknown[] = []): FormulaNode => ({
        $id: 'msg_' + getRandomName(),
        ...makeGetStub('checker-message')(arg),
    });

    static options: Array<[string, string, string]> = [
        ['good', 'Good', 'Flags'],
        ['amazing', 'Amazing', 'Flags'],
        ['warn', 'Warn', 'Flags'],
        ['bad', 'Bad', 'Flags'],
        ['terrible', 'Terrible', 'Flags'],
    ];

    renderLabel = (label: React.ReactNode, value: unknown) =>
        <span className='CheckerSelectMessage' data-type={value as string}>{label}</span>;

    render() {
        const { data, placeholder, path, dispatch, style, ...props } = this.props;

        return (
            <div {...(props as React.HTMLAttributes<HTMLDivElement>)} style={{ ...style, display: 'flex', minWidth: '20rem' }}>
                <Select
                    value={data.$value}
                    options={CheckerMessage.options}
                    onChange={event => data.change('$value', event.target.value)}
                    renderLabel={this.renderLabel}
                />
                <TextField
                    defaultValue={data.$args?.[0]}
                    onChange={val => data.change('$args[0]', val)}
                    placeholder={placeholder}
                    style={{ flex: '1 1 0' }}
                />
            </div>
        );
    }
}

export class CheckerVar extends PureComponent<{ data: UiFormulaNode }> {
    static getStub = makeGetStub('checker-var');

    render() {
        const { data } = this.props;

        return (
            <div>
                <TextField
                    defaultValue={data.$args?.[0]}
                    onChange={val => data.change('$args[0]', val)}
                    placeholder='name:'
                />
                :
                <TextField
                    defaultValue={data.$args?.[1]}
                    onChange={val => data.change('$args[1]', val)}
                    placeholder='value (math):'
                />
            </div>
        );
    }
}

const selectLog = (state: RootState, path: string, index: number): unknown => {
    const fullPath = path + '.$value.' + index;
    return (state.formula.log?.[fullPath] && state.formula.log[fullPath].result) || null;
};

interface CheckerTableProps {
    childResult?: string | null;
    data: UiFormulaNode;
}

class _CheckerTable extends PureComponent<CheckerTableProps> {
    static getStub = (): FormulaNode => ({
        $op: 'checker-table',
        $value: [
            FnExpression.getStub(['perServingExists']),
            FnExpression.getStub(['perServingExists']),
        ],
        $args: [
            CheckerMessage.getStub(['good']),
            CheckerMessage.getStub(['good']),
            CheckerMessage.getStub(['good']),
            CheckerMessage.getStub(['good']),
        ],
    });

    render() {
        const { childResult, data: { depth, getChildPath, log } } = this.props;

        const table = [
            [true, true],
            [true, false],
            [false, true],
            [false, false],
        ];

        const isSelected = (first: boolean, second: boolean) => {
            const _log = _.get(log, 'artifacts[0]');
            return !!_log && `${first}-${second}` === childResult;
        };

        return (
            <div className='expression-checker-table'>
                <div className='row-center'>
                    <Label>first:</Label>
                    <ExpressionSelector depth={depth + 1} path={getChildPath('$value.0')} />
                </div>
                <div className='row-center'>
                    <Label>second:</Label>
                    <ExpressionSelector depth={depth + 1} path={getChildPath('$value.1')} />
                </div>

                <div className={cx('expression-selector-table-header', 'row-center')}>
                    <Label children='first' />
                    <Label children='second' />
                    <Label children='message' />
                </div>

                {table.map(([first, second], index) =>
                    <div
                        key={index}
                        className={cx(
                            'row-center',
                            isSelected(first, second) && 'expression-checker-table-highlighted',
                        )}
                    >
                        <Label type={first}>{'' + first}</Label>
                        <Label type={second}>{'' + second}</Label>
                        <InlineCheckerMessage
                            path={getChildPath(`$args[${index}]`)}
                            placeholder={`message for ${first} × ${second}`}
                            style={{ flex: '1 1 0' }}
                        />
                    </div>,
                )}
            </div>
        );
    }
}

export const CheckerTable = connect((state: RootState, { data: { log, path } }: { data: UiFormulaNode }) => ({
    childResult: !log || !log.hasLog ? null :
        selectLog(state, path, 0) + '-' + selectLog(state, path, 1),
}))(_CheckerTable) as unknown as ExpressionComponent;

interface LabelProps {
    type?: unknown;
    children?: React.ReactNode;
    className?: string;
    [key: string]: unknown;
}

export const Label = ({ type, children, className, ...rest }: LabelProps) =>
    <div
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        data-value={'' + type}
        className={cx(className, 'expression-selector-table-label')}
    >{children}</div>;

export const InlineCheckerMessage = WithFormulaData(CheckerMessage as ComponentType<any>) as unknown as ComponentType<any>;

const checkerListSelectOptions: Array<[string, string]> = [
    ['always', 'Always'],
    ['never', 'Never'],
    ['some', 'At least one is true'],
    ['all', 'All are true'],
    ['none', 'None are true'],
];

export class CheckerList extends ListExpression {
    static KEY = 'checker-list';

    static getStub(data?: FormulaNode[]): FormulaNode {
        return { ...ListExpression.getStub.call(this, data), $trueIf: 'never' };
    }

    static renderActions = (data: UiFormulaNode) =>
        <div>
            <span style={{ marginLeft: '.2rem', fontWeight: 'normal' }}>is true when:</span>
            <Select
                value={data.$trueIf}
                options={checkerListSelectOptions}
                onChange={event => data.change('$trueIf', event.target.value)}
            />
        </div>;
}
